import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Download, Loader2, RefreshCw, ScrollText } from 'lucide-react';
import { isDifferentBuild, type AppBuildInfo } from '@nexops/shared';
import { useI18n } from '@/i18n/I18nProvider';
import { api } from '@/shared/lib/api';
import { APP_BUILD } from '@/shared/lib/app-version';
import { formatDate } from '@/shared/lib/format';
import { Button, Dialog, DialogContent } from '@/shared/ui';
import { ChangelogDialog } from './ChangelogDialog';
import { useChangelog } from './useChangelog';
import { fetchRemoteBuild } from './versionCheck';

interface HealthInfo {
  version?: string;
  sha?: string;
  builtAt?: string;
}

function Row({ label, build }: { label: string; build: { version?: string; sha?: string; builtAt?: string } | null }) {
  const { locale } = useI18n();
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-right text-sm">
        {build?.version ? (
          <>
            <span className="font-semibold tabular-nums">v{build.version}</span>
            <span className="ml-2 font-mono text-xs text-muted-foreground">{build.sha}</span>
            {build.builtAt && <span className="block text-[11px] text-muted-foreground">{formatDate(build.builtAt, locale, 'd MMM yyyy, HH:mm')}</span>}
          </>
        ) : (
          '—'
        )}
      </span>
    </div>
  );
}

/** „O aplikacji”: wersja panelu i backendu + ręczne sprawdzenie aktualizacji (te same dane, które sprawdza automat). */
export function AboutDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useI18n();
  const [result, setResult] = useState<'idle' | 'checking' | 'latest' | 'newer' | 'error'>('idle');
  const [remote, setRemote] = useState<AppBuildInfo | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const changelog = useChangelog(logOpen);
  const backend = useQuery({
    queryKey: ['app-about', 'backend'],
    queryFn: () => api.get<HealthInfo>('/health'),
    enabled: open,
    staleTime: 60_000,
    retry: false,
    meta: { silent: true },
  });

  const check = async () => {
    setResult('checking');
    const info = await fetchRemoteBuild();
    if (!info) return setResult('error');
    setRemote(info);
    setResult(isDifferentBuild(APP_BUILD, info) ? 'newer' : 'latest');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('appu.about.title')} description={t('appu.about.description')} closeLabel={t('common.close')} className="max-w-md">
        <div className="divide-y divide-border">
          <Row label={t('appu.about.panel')} build={APP_BUILD} />
          <Row label={t('appu.about.backend')} build={backend.data ?? null} />
        </div>

        <div className="mt-4 rounded-xl border border-border bg-muted/40 p-3">
          {result === 'latest' && (
            <p className="flex items-center gap-2 text-sm text-success">
              <CheckCircle2 className="size-4" aria-hidden />
              {t('appu.about.latest')}
            </p>
          )}
          {result === 'newer' && remote && (
            <div className="space-y-2">
              <p className="text-sm font-semibold">{t('appu.available', { version: remote.version })}</p>
              <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
                <Download />
                {t('appu.refreshNow')}
              </Button>
            </div>
          )}
          {result === 'error' && <p className="text-sm text-danger">{t('appu.about.error')}</p>}
          {(result === 'idle' || result === 'checking' || result === 'error') && (
            <Button variant="secondary" size="sm" className={result === 'error' ? 'mt-2' : ''} disabled={result === 'checking'} onClick={() => void check()}>
              {result === 'checking' ? <Loader2 className="animate-spin" /> : <RefreshCw />}
              {t('appu.about.check')}
            </Button>
          )}
          <p className="mt-2 text-[11px] text-muted-foreground">{t('appu.about.auto')}</p>
        </div>
        <Button variant="ghost" size="sm" className="mt-3" onClick={() => setLogOpen(true)}>
          <ScrollText />
          {t('appu.about.whatsNew')}
        </Button>
        <ChangelogDialog open={logOpen} onOpenChange={setLogOpen} entries={changelog.data?.slice(0, 8)} loading={changelog.isLoading} />
      </DialogContent>
    </Dialog>
  );
}
